/* eslint-disable sort-keys */
import Paper from '@mui/material/Paper';
import TableBody from '@mui/material/TableBody';
import TableCell, { TableCellProps } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow, { TableRowProps } from '@mui/material/TableRow';
import MuiTable, { TableProps as MuiTableProps } from '@mui/material/Table';
import { CenterLoader } from '@im/ui-lib/CenterLoader';
import Stack from '@mui/material/Stack';
import React, { useCallback, useMemo } from 'react';
import { SxProps, Theme, useTheme } from '@mui/system';
import { useTranslate } from '@im/react-localization';
import Typography from '@mui/material/Typography';
import { isEmbedded } from 'src/util';
import { HEADER_HEIGHT } from 'src/components/HeaderX4C';

const DATA_GRID_TOP = isEmbedded() ? '0px' : HEADER_HEIGHT;

type RenderCellProps<T> = {
  row: T;
  rowId: ReturnType<TableProps<T>['rowId']>;
  rowIndex: number;
  value: any;
};

type RenderHeaderProps<T> = {
  columnIndex: number;
  field: ColumnDef<T>['field'];
};

export type ColumnDef<T> = {
  field: Partial<keyof T> | string;
  renderCell: (props: RenderCellProps<T>) => React.ReactNode;
  renderHeader: (props?: RenderHeaderProps<T>) => React.ReactNode;
  tableCellProps?: TableCellProps;
  type?: string;
};

export interface TableProps<T> {
  /**
   * Set of columns of type ColumnDef<T>[].
   */
  columns: ColumnDef<T>[];
  loading?: boolean;
  loadingComponent?: React.ReactNode;
  onRowClick?: (rows: T) => void;
  paginationComponent?: React.ReactNode;
  /**
   * Return the id of a given T.
   */
  rowId: (row: T) => string | number;
  /**
   * Set of rows of type T[].
   */
  readonly rows: T[];
  selectedRows?: ReturnType<TableProps<T>['rowId']>[];
  sx?: SxProps<Theme>;
  tableProps?: MuiTableProps;
  tableRowProps?: TableRowProps;
}

const Table = <T extends { [key: string]: unknown }>(props: TableProps<T>) => {
  const theme = useTheme();
  const translate = useTranslate();
  const isSelected = useCallback(
    (rowId: ReturnType<TableProps<T>['rowId']>) =>
      props.selectedRows?.indexOf(rowId) !== -1,
    [props.selectedRows]
  );
  const loadingNode: React.ReactNode = useMemo(
    () => props.loadingComponent ?? <CenterLoader />,
    [props.loadingComponent]
  );

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{
          zIndex: 999,
          position: 'sticky',
          top: DATA_GRID_TOP,
          overflow: 'hidden',
          '.MuiSvgIcon-root': {
            color: '#0077d4',
          },
          borderRadius: 0,
          boxShadow: 'none',
          '.MuiTable-root': {
            tableLayout: 'fixed',
          },
          '.MuiTableHead-root': {
            background: '#F8F8F8;',
            whiteSpace: 'nowrap',
          },
          '.MuiTableCell-root': {
            padding: 0,
          },
          '.MuiTableCell-head': {
            borderBottom: '2px solid #0077D4;',
            fontSize: '16px',
            fontWeight: 600,
          },
          ...props.sx,
        }}
      >
        <MuiTable {...props?.tableProps}>
          <TableHead>
            <TableRow id="gds-vp-dd-table-head-container">
              {props.columns.map((c, i) => (
                <TableCell
                  data-field={c.field}
                  key={c.field.toString()}
                  {...c?.tableCellProps}
                >
                  <Stack
                    alignItems={'center'}
                    flexDirection={'row'}
                    gap={1}
                    minHeight={105}
                  >
                    {!props.loading &&
                      c.renderHeader({
                        field: c.field,
                        columnIndex: i,
                      })}
                  </Stack>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        </MuiTable>
      </TableContainer>
      <TableContainer
        component={Paper}
        id="gds-vp-dd-table-body-container"
        sx={{
          '.MuiSvgIcon-root': {
            color: '#0077d4',
          },
          borderRadius: 0,
          boxShadow: 'none',
          '.MuiTable-root': {
            tableLayout: 'fixed',
          },
          '.MuiTableCell-root': {
            padding: 0,
          },
          '.MuiTableCell-body': {
            height: 50,
            borderBottom: '2px solid #ddd;',
            padding: `0 ${theme.spacing(2.5)}`,
          },
          ...props.sx,
        }}
      >
        <MuiTable {...props?.tableProps}>
          <TableBody data-testid="gds-vp-table-body">
            {props.loading && (
              <TableRow>
                <TableCell align="center" colSpan={12}>
                  <Stack
                    alignItems={'center'}
                    justifyContent={'center'}
                    sx={{ height: '100%' }}
                  >
                    {loadingNode}
                  </Stack>
                </TableCell>
              </TableRow>
            )}
            {!props.loading && props.rows.length === 0 && (
              <TableRow>
                <TableCell align="center" colSpan={12}>
                  <Typography sx={{ fontWeight: 400 }} variant="error">
                    {translate('Common.NoRecord')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!props.loading &&
              props.rows.length !== 0 &&
              props.rows.map((row, index) => {
                const rowId = props.rowId(row);
                const _isSelected = isSelected(rowId);
                return (
                  <TableRow
                    hover
                    key={rowId}
                    selected={props.selectedRows && _isSelected}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    {...props?.tableRowProps}
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest('.MuiModal-root')) {
                        return;
                      }
                      e.stopPropagation();
                      e.preventDefault();
                      props?.onRowClick?.(row);
                    }}
                  >
                    {props.columns.map((col, i) => {
                      return (
                        <TableCell
                          component="th"
                          data-field={col.field}
                          key={col.field.toString()}
                          scope="row"
                          {...col?.tableCellProps}
                        >
                          {col.renderCell({
                            row,
                            rowId,
                            rowIndex: index,
                            value: row[col.field],
                          })}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
          </TableBody>
        </MuiTable>
      </TableContainer>
      {!props.loading && (
        <Stack flexDirection={'row'}>
          {props.selectedRows &&
            props.selectedRows.length > 0 &&
            `${props.selectedRows.length} rows selected`}
          {props?.paginationComponent}
        </Stack>
      )}
    </>
  );
};

export default Table;
